import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import debounce from 'lodash/debounce';
import Text from '../global/Text';
import fontMaker from '../../utils/font';
import { colors } from '../../constants/theme';
import { RootState } from '../../store/reducers';
import {
  applyLiteReportsFilters,
  fetchLiteGroupedReportsAsync,
  fetchLiteReportsAsync,
  setLiteReportsDateRange,
  setLiteReportsFilterQuery,
  setLiteReportsGroupBy
} from '../../store/reducers/siteReport/siteReport.action';
import {
  GroupedLiteReport,
  LiteReportDateRange,
  LiteReportGroupBy,
  ReportSummaryLite
} from '../../store/reducers/siteReport/type';
import { fetchUsersAsync } from '../../store/reducers/user/user';
import { fetchSitesAsync } from '../../store/reducers/site/site';
import { AllSites, AllUsers } from '../../utils/classes';
import type { ReportsFilterState, ReportSortType } from '../../typing/reportsFilter';
import { isReportsFilterActive } from '../../typing/reportsFilter';
import ReportsList from './ReportsList';
import ReportsGroupedList from './ReportsGroupedList';
import ReportsFilterModal from './ReportsFilterModal';
import { SCREEN_WIDTH } from '../../utils/screensize';

const DATE_RANGE_OPTIONS: { value: LiteReportDateRange; labelKey: string }[] = [
  { value: 'last24h', labelKey: 'reports.last24h' },
  { value: 'last7d', labelKey: 'reports.last7d' },
  { value: 'last30d', labelKey: 'reports.last30d' }
];

const GROUP_BY_OPTIONS: { value: LiteReportGroupBy; labelKey: string }[] = [
  { value: 'none', labelKey: 'reports.groupByNone' },
  { value: 'site', labelKey: 'reports.groupBySite' },
  { value: 'auditor', labelKey: 'reports.groupByAuditor' }
];

const ReportsScreen = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('home');

  const liteReports = useSelector<RootState, ReportSummaryLite[]>(s => s.siteReport.liteReports);
  const liteGroupedReports = useSelector<RootState, GroupedLiteReport[]>(s => s.siteReport.liteGroupedReports);
  const liteReportsLoading = useSelector<RootState, boolean>(s => s.siteReport.liteReportsLoading);
  const liteReportsTotal = useSelector<RootState, number>(s => s.siteReport.liteReportsTotal);
  const liteReportsPage = useSelector<RootState, number>(s => s.siteReport.liteReportsPage);
  const liteDateRange = useSelector<RootState, LiteReportDateRange>(s => s.siteReport.liteDateRange);
  const liteGroupBy = useSelector<RootState, LiteReportGroupBy>(s => s.siteReport.liteGroupBy);
  const liteFilterQuery = useSelector<RootState, string>(s => s.siteReport.liteFilterQuery);
  const liteFilterSites = useSelector<RootState, string[]>(s => s.siteReport.liteFilterSites);
  const liteFilterAuditors = useSelector<RootState, string[]>(s => s.siteReport.liteFilterAuditors);
  const liteSortType = useSelector<RootState, ReportSortType>(s => s.siteReport.liteSortType);
  const liteCustomStart = useSelector<RootState, string | null>(s => s.siteReport.liteCustomStart);
  const liteCustomEnd = useSelector<RootState, string | null>(s => s.siteReport.liteCustomEnd);
  const usersMap = useSelector<RootState, AllUsers>(s => s.user.users);
  const sitesMap = useSelector<RootState, AllSites | null | undefined>(s => s.site.sites);
  const profileColor = useSelector<RootState, string>(s => s.profiletheme.color);

  const searchRef = useRef<TextInput>(null);
  const [searchValue, setSearchValue] = useState(liteFilterQuery);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);

  useEffect(() => {
    setSearchValue(liteFilterQuery);
  }, [liteFilterQuery]);

  // Load the auditor (user) and site lists once for the Filter modal
  useEffect(() => {
    dispatch(fetchUsersAsync.request(1));
    dispatch(fetchSitesAsync.request(1));
  }, [dispatch]);

  const availableAuditors = useMemo(
    () =>
      Object.values(usersMap)
        .filter(u => !!u.userID)
        .map(u => ({
          id: u.userID as string,
          name: u.displayName || u.email || (u.userID as string),
          email: u.email || ''
        })),
    [usersMap]
  );

  // All sites the user can access (from /sites), independent of whether reports exist
  const availableSites = useMemo(
    () =>
      Object.values(sitesMap ?? {})
        .filter(s => !!s.siteID && !!s.name && !!s.name.trim())
        .map(s => ({ id: s.siteID as string, name: s.name.trim() }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [sitesMap]
  );

  const currentFilter: ReportsFilterState = useMemo(
    () => ({
      auditors: liteFilterAuditors,
      sites: liteFilterSites,
      datePreset: liteDateRange,
      customStart: liteCustomStart,
      customEnd: liteCustomEnd,
      sortType: liteSortType
    }),
    [liteFilterAuditors, liteFilterSites, liteDateRange, liteCustomStart, liteCustomEnd, liteSortType]
  );

  const hasActiveFilter = isReportsFilterActive(currentFilter);

  const handleApplyFilter = useCallback(
    (filter: ReportsFilterState) => {
      setFilterModalVisible(false);
      dispatch(applyLiteReportsFilters(filter));
    },
    [dispatch]
  );

  const dispatchFetch = useCallback(
    (clearList: boolean) => {
      if (liteGroupBy === 'none') {
        dispatch(fetchLiteReportsAsync.request({ clearList }));
      } else {
        dispatch(fetchLiteGroupedReportsAsync.request({ clearList }));
      }
    },
    [dispatch, liteGroupBy]
  );

  // Refetch when filters change
  useEffect(() => {
    dispatchFetch(true);
  }, [
    liteDateRange,
    liteGroupBy,
    liteFilterQuery,
    liteFilterSites,
    liteFilterAuditors,
    liteSortType,
    liteCustomStart,
    liteCustomEnd
  ]);

  // Refetch every time the tab is focused to get the latest reports
  useFocusEffect(
    useCallback(() => {
      dispatchFetch(true);
    }, [dispatchFetch])
  );

  const debouncedSearch = useCallback(
    debounce((text: string) => {
      dispatch(setLiteReportsFilterQuery(text));
    }, 500),
    [dispatch]
  );

  const handleSearchChange = (text: string) => {
    setSearchValue(text);
    debouncedSearch(text);
  };

  const handleClearSearch = () => {
    if (searchRef.current) {
      searchRef.current.clear();
    }
    setSearchValue('');
    debouncedSearch.cancel();
    if (liteFilterQuery !== '') {
      dispatch(setLiteReportsFilterQuery(''));
    }
  };

  const handleSelectDateRange = (range: LiteReportDateRange) => {
    if (range !== liteDateRange) {
      dispatch(setLiteReportsDateRange(range));
    }
  };

  const handleSelectGroupBy = (groupBy: LiteReportGroupBy) => {
    if (groupBy !== liteGroupBy) {
      dispatch(setLiteReportsGroupBy(groupBy));
    }
  };

  const handleRefresh = () => dispatchFetch(true);

  const handleLoadMore = () => {
    if (!liteReportsLoading && liteReportsPage <= liteReportsTotal) {
      dispatchFetch(false);
    }
  };

  const hasMore = liteReportsPage <= liteReportsTotal;

  return (
    <View style={styles.root}>
      <View style={styles.filterPanel}>
        {/* Search bar + filter button */}
        <View style={styles.searchContainer}>
          <View style={styles.searchRow}>
            <View style={[styles.searchBox, isSearchFocused ? styles.searchBoxFocused : null]}>
              <Icon
                name="magnify"
                size={18}
                color={isSearchFocused ? colors.primary : '#9CA3AF'}
                style={styles.searchIcon}
              />
              <TextInput
                ref={searchRef}
                style={styles.searchInput}
                placeholder={t('reports.searchPlaceholder')}
                placeholderTextColor="#B2B8C2"
                onChangeText={handleSearchChange}
                value={searchValue}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
              />
              {!!searchValue && (
                <TouchableOpacity onPress={handleClearSearch} style={styles.clearBtn}>
                  <Icon name="close-circle" size={16} color="#9CA3AF" />
                </TouchableOpacity>
              )}
            </View>
            <TouchableOpacity
              style={[styles.filterButton, hasActiveFilter ? styles.filterButtonActive : null]}
              onPress={() => setFilterModalVisible(true)}
              activeOpacity={0.7}
            >
              <Icon name="tune-vertical" size={22} color={hasActiveFilter ? colors.primary : '#535353'} />
              {hasActiveFilter && <View style={styles.filterDot} />}
            </TouchableOpacity>
          </View>
        </View>

        {/* Date range filter */}
        <View style={styles.dateRangeRow}>
          {DATE_RANGE_OPTIONS.map(opt => (
            <TouchableOpacity
              key={opt.value}
              style={[styles.dateRangeBtn, liteDateRange === opt.value && styles.dateRangeBtnActive]}
              onPress={() => handleSelectDateRange(opt.value)}
            >
              <Text style={[styles.dateRangeLabel, liteDateRange === opt.value && styles.dateRangeLabelActive]}>
                {t(opt.labelKey)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Group By */}
        <View style={styles.groupBySection}>
          <Text style={styles.groupByTitle}>{t('reports.groupBy')}</Text>
          <View style={styles.groupByOptions}>
            {GROUP_BY_OPTIONS.map(opt => (
              <TouchableOpacity
                key={opt.value}
                style={[styles.groupByChip, liteGroupBy === opt.value && styles.groupByChipActive]}
                onPress={() => handleSelectGroupBy(opt.value)}
              >
                <Text style={[styles.groupByLabel, liteGroupBy === opt.value && styles.groupByLabelActive]}>
                  {t(opt.labelKey)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.divider} />
      </View>

      {/* Loading overlay for initial load */}
      {liteReportsLoading && liteReports.length === 0 && liteGroupedReports.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator color={profileColor} size="large" />
        </View>
      ) : liteGroupBy === 'none' ? (
        <ReportsList
          reports={liteReports}
          loading={liteReportsLoading}
          hasMore={hasMore}
          profileColor={profileColor}
          onRefresh={handleRefresh}
          onLoadMore={handleLoadMore}
        />
      ) : (
        <ReportsGroupedList
          groups={liteGroupedReports}
          loading={liteReportsLoading}
          hasMore={hasMore}
          profileColor={profileColor}
          onRefresh={handleRefresh}
          onLoadMore={handleLoadMore}
        />
      )}

      {/* Filter & Sort modal */}
      <ReportsFilterModal
        visible={filterModalVisible}
        initialFilter={currentFilter}
        availableAuditors={availableAuditors}
        availableSites={availableSites}
        onApply={handleApplyFilter}
        onClose={() => setFilterModalVisible(false)}
      />
    </View>
  );
};

export default ReportsScreen;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#FFFFFF'
  },
  filterPanel: {
    marginHorizontal: 16,
    marginTop: 16,
    marginBottom: 8,
    paddingBottom: 6
  },
  searchContainer: {
    paddingBottom: 10
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  searchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#D9E1E8',
    paddingHorizontal: 14,
    height: 46
  },
  filterButton: {
    width: 46,
    height: 46,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#D9E1E8',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 10
  },
  filterButtonActive: {
    borderColor: colors.primary,
    backgroundColor: '#F1FCF5'
  },
  filterDot: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primary
  },
  searchBoxFocused: {
    borderColor: colors.primary,
    backgroundColor: '#FFFFFF'
  },
  searchIcon: {
    marginRight: 8
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#111827',
    ...fontMaker({ weight: 'Regular' })
  },
  clearBtn: {
    padding: 4,
    marginLeft: 8
  },
  dateRangeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 6
  },
  dateRangeBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#E1E7EE',
    backgroundColor: '#fff',
    marginRight: 8,
    marginBottom: 8
  },
  dateRangeBtnActive: {
    borderColor: '#3CD070',
    backgroundColor: '#F1FCF5'
  },
  dateRangeLabel: {
    fontSize: 13,
    color: '#374151',
    ...fontMaker({ weight: 'Regular' })
  },
  dateRangeLabelActive: {
    color: '#3CD070',
    ...fontMaker({ weight: 'SemiBold' })
  },
  groupBySection: {
    paddingTop: 2
  },
  groupByTitle: {
    fontSize: 13,
    color: '#4B5563',
    ...fontMaker({ weight: 'SemiBold' }),
    marginBottom: 8
  },
  groupByOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  groupByChip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#E1E7EE',
    backgroundColor: '#FFFFFF',
    marginRight: 8,
    marginBottom: 8
  },
  groupByChipActive: {
    borderColor: '#3CD070',
    backgroundColor: '#F1FCF5'
  },
  groupByLabel: {
    fontSize: 13,
    color: '#374151',
    ...fontMaker({ weight: 'Regular' })
  },
  groupByLabelActive: {
    color: '#3CD070',
    ...fontMaker({ weight: 'SemiBold' })
  },
  divider: {
    height: 1,
    backgroundColor: '#EEF2F6',
    marginTop: 2
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  }
});
